import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, Plus, Play, Pause, Trash2, X, Mail, MessageSquare,
  PhoneCall, Linkedin, Clock, Users, BarChart3, Zap, ChevronRight,
  Send, Target, Sparkles, GripVertical, ChevronDown, ChevronUp, UserPlus
} from 'lucide-react';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const CHANNEL_CONFIG = {
  email: { label: 'Email', icon: Mail, color: 'text-blue-400 bg-blue-500/15 border-blue-500/30' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30' },
  voice_call: { label: 'Voice Call', icon: PhoneCall, color: 'text-amber-400 bg-amber-500/15 border-amber-500/30' },
  linkedin: { label: 'LinkedIn', icon: Linkedin, color: 'text-sky-400 bg-sky-500/15 border-sky-500/30' },
};

const STATUS_BADGE = {
  draft: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  completed: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};

const GOAL_OPTIONS = [
  { value: 'book_meetings', label: 'Book Meetings' },
  { value: 'generate_leads', label: 'Generate Leads' },
  { value: 're_engage', label: 'Re-engage Cold Leads' },
  { value: 'follow_up', label: 'Follow Up' },
];

const SalesOutreach = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, detail, create
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Create form
  const [form, setForm] = useState({
    name: '', description: '', goal: 'book_meetings',
    send_window_start: '09:00', send_window_end: '17:00',
    send_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    timezone: 'America/New_York',
    exit_on_reply: true, exit_on_meeting: true,
    sequence_steps: [],
  });
  const [aiPrompt, setAiPrompt] = useState({ goal: 'book_meetings', audience: '', tone: 'professional', channels: ['email', 'sms', 'voice_call'] });
  const [generatingAI, setGeneratingAI] = useState(false);
  const [promoTemplates, setPromoTemplates] = useState([]);
  const [showPromo, setShowPromo] = useState(false);
  const [creatingPromo, setCreatingPromo] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/outreach/campaigns`, { headers });
      setCampaigns(res.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/outreach/stats`, { headers });
      setStats(res.data);
    } catch { /* */ }
  }, [token]);

  useEffect(() => { fetchCampaigns(); fetchStats(); }, [fetchCampaigns, fetchStats]);

  const fetchPromoTemplates = async () => {
    try {
      const res = await axios.get(`${API}/outreach/promo-templates`, { headers });
      setPromoTemplates(res.data);
      setShowPromo(true);
    } catch {
      toast({ title: 'Failed to load promo templates', variant: 'destructive' });
    }
  };

  const createPromo = async (templateId, contactFilter) => {
    setCreatingPromo(templateId);
    try {
      const res = await axios.post(`${API}/outreach/promo-campaign`, { template_id: templateId, contact_filter: contactFilter }, { headers });
      toast({ title: 'Promo campaign created!' });
      setShowPromo(false);
      fetchCampaigns();
      fetchStats();
      fetchCampaignDetail(res.data.id);
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setCreatingPromo(null); }
  };

  const fetchCampaignDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/outreach/campaigns/${id}`, { headers });
      setCampaignDetail(res.data);
      setSelectedCampaign(id);
      setView('detail');
    } catch {
      toast({ title: 'Failed to load campaign', variant: 'destructive' });
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Campaign name is required', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        ...form,
        sequence_steps: form.sequence_steps.map((s, i) => ({ ...s, step_number: i + 1 })),
      };
      const res = await axios.post(`${API}/outreach/campaigns`, payload, { headers });
      toast({ title: 'Campaign created!' });
      setView('list');
      resetForm();
      fetchCampaigns();
      fetchStats();
      fetchCampaignDetail(res.data.id);
    } catch (err) {
      toast({ title: 'Failed to create campaign', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/outreach/campaigns/${id}`, { headers });
      toast({ title: 'Campaign deleted' });
      setView('list');
      setSelectedCampaign(null);
      setCampaignDetail(null);
      fetchCampaigns();
      fetchStats();
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleActivate = async (id) => {
    try {
      await axios.post(`${API}/outreach/campaigns/${id}/activate`, {}, { headers });
      toast({ title: 'Campaign activated!' });
      fetchCampaignDetail(id);
      fetchCampaigns();
      fetchStats();
    } catch (err) {
      toast({ title: 'Cannot activate', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handlePause = async (id) => {
    try {
      await axios.post(`${API}/outreach/campaigns/${id}/pause`, {}, { headers });
      toast({ title: 'Campaign paused' });
      fetchCampaignDetail(id);
      fetchCampaigns();
    } catch {
      toast({ title: 'Failed to pause', variant: 'destructive' });
    }
  };

  const handleEnroll = async () => {
    if (!selectedContacts.length) return;
    try {
      await axios.post(`${API}/outreach/campaigns/${selectedCampaign}/enroll`, { contact_ids: selectedContacts }, { headers });
      toast({ title: `${selectedContacts.length} contact(s) enrolled` });
      setShowEnrollModal(false);
      setSelectedContacts([]);
      fetchCampaignDetail(selectedCampaign);
    } catch (err) {
      toast({ title: 'Enrollment failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const openEnrollModal = async () => {
    try {
      const res = await axios.get(`${API}/crm/contacts`, { headers });
      const enrolled = campaignDetail?.campaign?.enrolled_contacts || [];
      setAvailableContacts(res.data.filter(c => !enrolled.includes(c.id)));
      setShowEnrollModal(true);
    } catch {
      toast({ title: 'Failed to load contacts', variant: 'destructive' });
    }
  };

  const handleUnenroll = async (contactIds) => {
    try {
      await axios.post(`${API}/outreach/campaigns/${selectedCampaign}/unenroll`, { contact_ids: contactIds }, { headers });
      toast({ title: 'Contact removed from campaign' });
      fetchCampaignDetail(selectedCampaign);
    } catch { /* */ }
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.audience.trim()) {
      toast({ title: 'Describe your target audience', variant: 'destructive' });
      return;
    }
    setGeneratingAI(true);
    try {
      const res = await axios.post(`${API}/outreach/ai-sequence`, aiPrompt, { headers });
      setForm(f => ({ ...f, sequence_steps: res.data.steps }));
      toast({ title: 'AI sequence generated!' });
    } catch {
      toast({ title: 'Failed to generate sequence', variant: 'destructive' });
    } finally {
      setGeneratingAI(false);
    }
  };

  const addStep = (channel) => {
    setForm(f => ({
      ...f,
      sequence_steps: [...f.sequence_steps, {
        id: crypto.randomUUID(),
        step_number: f.sequence_steps.length + 1,
        channel,
        subject: '',
        content: '',
        delay_days: f.sequence_steps.length > 0 ? 3 : 0,
        delay_hours: 0,
      }]
    }));
  };

  const updateStep = (idx, field, value) => {
    setForm(f => ({
      ...f,
      sequence_steps: f.sequence_steps.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };

  const removeStep = (idx) => {
    setForm(f => ({ ...f, sequence_steps: f.sequence_steps.filter((_, i) => i !== idx) }));
  };

  const resetForm = () => {
    setForm({ name: '', description: '', goal: 'book_meetings', send_window_start: '09:00', send_window_end: '17:00', send_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], timezone: 'America/New_York', exit_on_reply: true, exit_on_meeting: true, sequence_steps: [] });
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      send_days: f.send_days.includes(day) ? f.send_days.filter(d => d !== day) : [...f.send_days, day]
    }));
  };

  // ---- RENDER ----

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="sales-outreach-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            {view !== 'list' && (
              <Button variant="ghost" onClick={() => setView('list')} className="text-gray-400 hover:text-white" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Campaigns
              </Button>
            )}
            <h1 className="text-xl font-bold text-white">Sales Outreach</h1>
          </div>
          {view === 'list' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchPromoTemplates} className="border-white/10 text-gray-300 hover:text-white" data-testid="promo-templates-btn">
                <Zap className="w-4 h-4 mr-2" /> Promo Templates
              </Button>
              <Button onClick={() => { resetForm(); setView('create'); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-campaign-btn">
                <Plus className="w-4 h-4 mr-2" /> New Campaign
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Bar */}
        {view === 'list' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="outreach-stats">
            {[
              { label: 'Campaigns', value: stats.total_campaigns || 0, icon: Target },
              { label: 'Active', value: stats.active_campaigns || 0, icon: Play },
              { label: 'Enrolled', value: stats.total_enrolled || 0, icon: Users },
              { label: 'Meetings', value: stats.meetings_booked || 0, icon: Zap },
            ].map(s => (
              <div key={s.label} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* LIST VIEW */}
        {view === 'list' && (
          <div className="space-y-3" data-testid="campaign-list">
            {loading ? (
              <div className="text-center py-16 text-gray-400">Loading campaigns...</div>
            ) : campaigns.length === 0 ? (
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="py-16 text-center">
                  <Send className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2" data-testid="no-campaigns-msg">No campaigns yet</h3>
                  <p className="text-gray-400 mb-4">Create your first outreach campaign to start reaching prospects.</p>
                  <Button onClick={() => { resetForm(); setView('create'); }} className="bg-purple-600 hover:bg-purple-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Create Campaign
                  </Button>
                </CardContent>
              </Card>
            ) : (
              campaigns.map(c => (
                <div
                  key={c.id}
                  onClick={() => fetchCampaignDetail(c.id)}
                  className="bg-gray-900/50 border border-white/10 hover:border-purple-500/30 rounded-lg p-4 cursor-pointer transition-all flex items-center gap-4"
                  data-testid={`campaign-row-${c.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Send className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-white font-medium truncate">{c.name}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>{c.status}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{c.sequence_steps?.length || 0} steps</span>
                      <span>{c.enrolled_contacts?.length || 0} contacts</span>
                      {c.goal && <span className="capitalize">{c.goal.replace('_', ' ')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(c.sequence_steps || []).slice(0, 3).map((s, i) => {
                      const ch = CHANNEL_CONFIG[s.channel];
                      return ch ? <ch.icon key={i} className="w-3.5 h-3.5 text-gray-500" /> : null;
                    })}
                    <ChevronRight className="w-4 h-4 text-gray-600 ml-1" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* CREATE VIEW */}
        {view === 'create' && (
          <div className="max-w-3xl mx-auto space-y-6" data-testid="create-campaign-form">
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader><CardTitle className="text-white">Campaign Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-400 text-xs">Campaign Name *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g., Q1 Outreach — SaaS Founders" className="bg-black/30 border-white/10 text-white" data-testid="campaign-name-input" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Description</Label>
                  <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="What's this campaign about?" className="bg-black/30 border-white/10 text-white min-h-[60px]" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Goal</Label>
                  <Select value={form.goal} onValueChange={v => setForm(f => ({...f, goal: v}))}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-900 border-white/10">
                      {GOAL_OPTIONS.map(g => <SelectItem key={g.value} value={g.value} className="text-white">{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Send Window */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader><CardTitle className="text-white text-base">Send Window</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-xs">Start Time</Label>
                    <Input type="time" value={form.send_window_start} onChange={e => setForm(f => ({...f, send_window_start: e.target.value}))} className="bg-black/30 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">End Time</Label>
                    <Input type="time" value={form.send_window_end} onChange={e => setForm(f => ({...f, send_window_end: e.target.value}))} className="bg-black/30 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs mb-2 block">Send Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <button
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          form.send_days.includes(day) ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-white/5 text-gray-500 border border-white/10'
                        }`}
                        data-testid={`day-${day.toLowerCase()}`}
                      >{day}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.exit_on_reply} onChange={e => setForm(f => ({...f, exit_on_reply: e.target.checked}))} className="rounded border-white/20" />
                    <span className="text-gray-400">Exit on reply</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.exit_on_meeting} onChange={e => setForm(f => ({...f, exit_on_meeting: e.target.checked}))} className="rounded border-white/20" />
                    <span className="text-gray-400">Exit on meeting booked</span>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* AI Sequence Generator */}
            <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/20">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" /> AI Sequence Generator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-gray-400 text-xs">Target Audience *</Label>
                  <Input value={aiPrompt.audience} onChange={e => setAiPrompt(p => ({...p, audience: e.target.value}))} placeholder="e.g., SaaS founders with 10-50 employees" className="bg-black/30 border-white/10 text-white" data-testid="ai-audience-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-xs">Goal</Label>
                    <Select value={aiPrompt.goal} onValueChange={v => setAiPrompt(p => ({...p, goal: v}))}>
                      <SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-gray-900 border-white/10">
                        {GOAL_OPTIONS.map(g => <SelectItem key={g.value} value={g.value} className="text-white">{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Tone</Label>
                    <Select value={aiPrompt.tone} onValueChange={v => setAiPrompt(p => ({...p, tone: v}))}>
                      <SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-gray-900 border-white/10">
                        <SelectItem value="professional" className="text-white">Professional</SelectItem>
                        <SelectItem value="casual" className="text-white">Casual</SelectItem>
                        <SelectItem value="formal" className="text-white">Formal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs mb-2 block">Channels</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(CHANNEL_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => setAiPrompt(p => ({...p, channels: p.channels.includes(key) ? p.channels.filter(c => c !== key) : [...p.channels, key]}))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          aiPrompt.channels.includes(key) ? cfg.color : 'bg-white/5 text-gray-500 border-white/10'
                        }`}
                      >
                        <cfg.icon className="w-3 h-3" /> {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={handleGenerateAI} disabled={generatingAI} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="generate-ai-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> {generatingAI ? 'Generating...' : 'Generate AI Sequence'}
                </Button>
              </CardContent>
            </Card>

            {/* Sequence Steps */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base">Sequence Steps</CardTitle>
                  <span className="text-xs text-gray-500">{form.sequence_steps.length} step{form.sequence_steps.length !== 1 ? 's' : ''}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {form.sequence_steps.map((step, idx) => {
                  const ch = CHANNEL_CONFIG[step.channel] || CHANNEL_CONFIG.email;
                  return (
                    <div key={step.id || idx} className="border border-white/10 rounded-lg p-4 space-y-3" data-testid={`step-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-gray-600" />
                          <span className="text-xs text-gray-500 font-medium">Step {idx + 1}</span>
                          <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${ch.color}`}>
                            <ch.icon className="w-3 h-3" /> {ch.label}
                          </span>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => removeStep(idx)} className="text-gray-500 hover:text-red-400 h-7 w-7 p-0">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {idx > 0 && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-gray-500" />
                          <span className="text-xs text-gray-500">Wait</span>
                          <Input type="number" min="0" value={step.delay_days} onChange={e => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)} className="bg-black/30 border-white/10 text-white h-7 w-16 text-xs" />
                          <span className="text-xs text-gray-500">days</span>
                        </div>
                      )}
                      {step.channel === 'email' && (
                        <Input value={step.subject} onChange={e => updateStep(idx, 'subject', e.target.value)} placeholder="Email subject..." className="bg-black/30 border-white/10 text-white text-sm h-8" />
                      )}
                      <Textarea value={step.content} onChange={e => updateStep(idx, 'content', e.target.value)} placeholder={step.channel === 'voice_call' ? 'Call script / talking points...' : 'Message content...'} className="bg-black/30 border-white/10 text-white min-h-[80px] text-sm" />
                    </div>
                  );
                })}

                {/* Add step buttons */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {Object.entries(CHANNEL_CONFIG).map(([key, cfg]) => (
                    <Button key={key} variant="outline" size="sm" onClick={() => addStep(key)} className="border-white/10 text-gray-400 hover:text-white text-xs" data-testid={`add-${key}-step`}>
                      <cfg.icon className="w-3 h-3 mr-1.5" /> {cfg.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setView('list')} className="border-white/10 text-gray-400 hover:text-white flex-1">Cancel</Button>
              <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-700 text-white flex-1" data-testid="save-campaign-btn">Create Campaign</Button>
            </div>
          </div>
        )}

        {/* DETAIL VIEW */}
        {view === 'detail' && campaignDetail && (
          <div className="max-w-4xl mx-auto space-y-6" data-testid="campaign-detail">
            {/* Campaign Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold text-white" data-testid="campaign-name">{campaignDetail.campaign.name}</h2>
                  <span className={`text-xs px-2.5 py-1 rounded-full border capitalize ${STATUS_BADGE[campaignDetail.campaign.status] || STATUS_BADGE.draft}`}>
                    {campaignDetail.campaign.status}
                  </span>
                </div>
                {campaignDetail.campaign.description && (
                  <p className="text-gray-400 text-sm">{campaignDetail.campaign.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                {campaignDetail.campaign.status === 'draft' || campaignDetail.campaign.status === 'paused' ? (
                  <Button size="sm" onClick={() => handleActivate(selectedCampaign)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="activate-btn">
                    <Play className="w-3.5 h-3.5 mr-1.5" /> Activate
                  </Button>
                ) : campaignDetail.campaign.status === 'active' ? (
                  <Button size="sm" onClick={() => handlePause(selectedCampaign)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="pause-btn">
                    <Pause className="w-3.5 h-3.5 mr-1.5" /> Pause
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => handleDelete(selectedCampaign)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-campaign-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Campaign Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Enrolled', value: campaignDetail.campaign.stats?.total_enrolled || 0 },
                { label: 'Emails Sent', value: campaignDetail.campaign.stats?.emails_sent || 0 },
                { label: 'SMS Sent', value: campaignDetail.campaign.stats?.sms_sent || 0 },
                { label: 'Calls Made', value: campaignDetail.campaign.stats?.calls_made || 0 },
              ].map(s => (
                <div key={s.label} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-white">{s.value}</p>
                  <p className="text-[10px] text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Sequence Steps & Settings */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <Card className="bg-gray-900/50 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Sequence Steps</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(campaignDetail.campaign.sequence_steps || []).length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">No steps defined</p>
                    ) : (
                      campaignDetail.campaign.sequence_steps.map((step, idx) => {
                        const ch = CHANNEL_CONFIG[step.channel] || CHANNEL_CONFIG.email;
                        return (
                          <div key={idx} className="flex items-start gap-3">
                            {idx > 0 && (
                              <div className="flex flex-col items-center mr-1">
                                <div className="w-px h-4 bg-white/10" />
                                <div className="text-[9px] text-gray-600 my-1">{step.delay_days}d</div>
                                <div className="w-px h-4 bg-white/10" />
                              </div>
                            )}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${ch.color} flex-1`}>
                              <ch.icon className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="font-medium">Step {idx + 1}: {ch.label}</span>
                              {step.subject && <span className="text-gray-400 truncate ml-2">— {step.subject}</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>

                {/* Enrolled Contacts */}
                <Card className="bg-gray-900/50 border-white/10">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white text-base">Enrolled Contacts ({campaignDetail.enrolled_contacts?.length || 0})</CardTitle>
                      <Button size="sm" onClick={openEnrollModal} className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="enroll-contacts-btn">
                        <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Enroll
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(campaignDetail.enrolled_contacts || []).length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">No contacts enrolled. Add contacts from your CRM.</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {campaignDetail.enrolled_contacts.map(c => (
                          <div key={c.id} className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                                <span className="text-white text-xs font-semibold">{(c.name || '?')[0].toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">{c.name || 'Unknown'}</p>
                                <p className="text-gray-500 text-xs">{c.email || c.phone || ''}</p>
                              </div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => handleUnenroll([c.id])} className="text-gray-500 hover:text-red-400 h-7 w-7 p-0">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Settings Sidebar */}
              <div>
                <Card className="bg-gray-900/50 border-white/10 sticky top-24">
                  <CardHeader><CardTitle className="text-white text-base">Settings</CardTitle></CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between text-gray-400">
                      <span>Goal</span>
                      <span className="text-white capitalize">{(campaignDetail.campaign.goal || '').replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Send Window</span>
                      <span className="text-white">{campaignDetail.campaign.send_window_start} — {campaignDetail.campaign.send_window_end}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Send Days</span>
                      <span className="text-white">{(campaignDetail.campaign.send_days || []).join(', ')}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Exit on Reply</span>
                      <span className="text-white">{campaignDetail.campaign.exit_on_reply ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Exit on Meeting</span>
                      <span className="text-white">{campaignDetail.campaign.exit_on_meeting ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Created</span>
                      <span className="text-white text-xs">{campaignDetail.campaign.created_at ? new Date(campaignDetail.campaign.created_at).toLocaleDateString() : ''}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="enroll-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md max-h-[80vh] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Enroll Contacts</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => { setShowEnrollModal(false); setSelectedContacts([]); }} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto space-y-2">
              {availableContacts.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No available contacts. Add contacts in the CRM first.</p>
              ) : (
                availableContacts.map(c => (
                  <label key={c.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                    selectedContacts.includes(c.id) ? 'bg-purple-500/10 border-purple-500/30' : 'bg-black/20 border-white/5 hover:border-white/10'
                  }`}>
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(c.id)}
                      onChange={() => setSelectedContacts(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id])}
                      className="rounded border-white/20"
                    />
                    <div>
                      <p className="text-white text-sm font-medium">{c.name || 'Unknown'}</p>
                      <p className="text-gray-500 text-xs">{c.email || c.phone || ''}</p>
                    </div>
                  </label>
                ))
              )}
            </CardContent>
            {availableContacts.length > 0 && (
              <div className="p-4 border-t border-white/10 flex-shrink-0">
                <Button onClick={handleEnroll} disabled={!selectedContacts.length} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="confirm-enroll-btn">
                  Enroll {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}
      {/* Promo Templates Modal */}
      {showPromo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="promo-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-2xl max-h-[85vh] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> Promo Campaign Templates</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setShowPromo(false)} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
              <p className="text-gray-500 text-sm">Launch a pre-built campaign in one click. Contacts will be auto-enrolled based on your CRM data.</p>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto space-y-3">
              {promoTemplates.map(t => (
                <div key={t.id} className="border border-white/10 rounded-lg p-4 hover:border-purple-500/30 transition-all" data-testid={`promo-${t.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-white font-medium">{t.name}</h4>
                      <p className="text-gray-500 text-xs mt-0.5">{t.description}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full capitalize">{t.goal.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    {t.steps.map((s, i) => {
                      const ch = CHANNEL_CONFIG[s.channel];
                      return ch ? (
                        <span key={i} className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${ch.color}`}>
                          <ch.icon className="w-3 h-3" /> Step {i+1}
                        </span>
                      ) : null;
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => createPromo(t.id, 'cold')} disabled={creatingPromo === t.id} className="bg-purple-600 hover:bg-purple-700 text-white text-xs flex-1" data-testid={`launch-${t.id}-cold`}>
                      {creatingPromo === t.id ? 'Creating...' : 'Launch (Cold Leads)'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => createPromo(t.id, 'lost')} disabled={creatingPromo === t.id} className="border-white/10 text-gray-300 text-xs flex-1" data-testid={`launch-${t.id}-lost`}>
                      Launch (Lost Leads)
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SalesOutreach;
